import React, { useState } from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import moment from "moment";
import MonthYearPicker from "@/components/ui/month-year-picker";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { parse, isValid } from "date-fns";
import { formatLong } from "@/utils/date";
import { getCurrentLanguage } from "@/i18n";
import { useTranslation } from "react-i18next";

const UNSELECTED_BORDER = "#C3C6CC";

interface ExamBookingSectionProps {
  hasBookedExam: boolean | null;
  onBookedChange: (value: boolean) => void;
  /** Raw input value: "YYYY-MM-DD" when booked, "YYYY-MM" when not booked, "" if unset */
  dateInputValue: string;
  onDateChange: (rawValue: string) => void;
  minDate?: string;
  maxDate?: string;
  minMonth?: string;
  maxMonth?: string;
  variant?: "create" | "edit";
}

const ExamBookingSection: React.FC<ExamBookingSectionProps> = ({
  hasBookedExam,
  onBookedChange,
  dateInputValue,
  onDateChange,
  minDate,
  maxDate,
  minMonth,
  maxMonth,
  variant = "create",
}) => {
  const { t } = useTranslation();
  const isCreate = variant === "create";
  const inputHeight = isCreate ? "h-[50px]" : "h-10";
  const inputPadding = isCreate ? "px-4 py-3" : "px-3 py-2";
  const inputText = isCreate ? "text-base" : "text-sm";
  const [calendarOpen, setCalendarOpen] = useState(false);

  const selectedDate =
    dateInputValue && hasBookedExam === true
      ? parse(dateInputValue, "yyyy-MM-dd", new Date())
      : undefined;
  const validSelectedDate =
    selectedDate && isValid(selectedDate) ? selectedDate : undefined;

  const minDateObj = minDate ? parse(minDate, "yyyy-MM-dd", new Date()) : undefined;
  const maxDateObj = maxDate ? parse(maxDate, "yyyy-MM-dd", new Date()) : undefined;

  const displayValue =
    validSelectedDate
      ? formatLong(
          moment(dateInputValue, "YYYY-MM-DD").toDate(),
          getCurrentLanguage(),
        )
      : "";

  return (
    <div className="flex flex-col gap-3">
      {/* Question */}
      <label
        className={"font-medium font-inter text-zinc-950 text-sm"}
      >
        {t("profileCreate.exam_booking_question")}
      </label>

      {/* Yes / No buttons */}
      <div className="flex gap-2.5">
        {[
          { label: t("common.yes"), value: true },
          { label: t("common.no"), value: false },
        ].map(({ label, value }) => {
          const selected = hasBookedExam === value;
          return (
            <button
              key={label}
              type="button"
              onClick={() => onBookedChange(value)}
              style={{
                borderColor: selected ? "var(--dp-brand-button)" : UNSELECTED_BORDER,
                backgroundColor: selected ? "var(--dp-brand-soft)" : "white",
                color: selected ? "var(--dp-brand-button)" : "#1A1A1A",
              }}
              className={cn(
                "flex-1 rounded-lg border font-medium font-inter transition-colors",
                isCreate ? "py-3 text-base" : "py-2.5 text-sm"
              )}
            >
              {label}
            </button>
          );
        })}
      </div>

      {/* Date picker — shown once Yes or No is selected */}
      {hasBookedExam !== null && (
        <div className="flex flex-col gap-1.5">
          <label className="text-[#666666] text-sm font-normal font-inter">
            {hasBookedExam
              ? t("profileCreate.exam_booking_date_label")
              : t("profileCreate.exam_booking_month_label")}
          </label>

          {hasBookedExam === true ? (
            /* Full date picker */
            <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className={cn(
                    "w-full flex items-center justify-between bg-white border border-[#efefef] rounded-md font-inter text-left",
                    inputHeight,
                    inputPadding,
                    inputText
                  )}
                >
                  <span className={cn(!displayValue && "text-[#9EA3AB]", "font-normal")}>
                    {displayValue || t("profileCreate.exam_booking_select_date")}
                  </span>
                  <ChevronDown className="w-4 h-4 text-[#9EA3AB] shrink-0" />
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={validSelectedDate}
                  onSelect={(date) => {
                    if (date) {
                      onDateChange(moment(date).format("YYYY-MM-DD"));
                    }
                    setCalendarOpen(false);
                  }}
                  disabled={(date) => {
                    if (minDateObj && date < minDateObj) return true;
                    if (maxDateObj && date > maxDateObj) return true;
                    return false;
                  }}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          ) : (
            /* Month / year picker */
            <MonthYearPicker
              value={dateInputValue}
              onChange={onDateChange}
              min={minMonth}
              max={maxMonth}
              triggerClassName={cn(inputHeight, inputPadding, inputText)}
            />
          )}

        </div>
      )}
    </div>
  );
};

export default ExamBookingSection;
